import axios, { AxiosResponse } from "axios";
import { InstitutionUrl } from "./AUrl";

/*
The interface for the response from the create department endpoint.

@id: The department ID.
@name: The name of the department.
@institution_id: The ID of the associated institution.
*/
export interface CreateDepartmentResponse {
    id: number;
    name: string;
    institution_id: number;
}

/*
The interface for the API response.

@status: The HTTP status code.
@error: The error message if any.
@department: The created department if applicable.
*/
export interface CreateDepartmentApiResponse {
    status: number;
    error?: string;
    department?: CreateDepartmentResponse;
}

// Function Signature
export type CreateDepartmentType = (
    jwt: string,
    institutionId: number,
    name: string,
) => Promise<CreateDepartmentApiResponse>;

/*
The function to create a department within an institution.

@jwt: The JWT of the user making the request.
@institutionId: The ID of the institution where the department will be created.
@name: The name of the department.

@returns: The ApiResponse from the create department endpoint.
*/
export const createDepartment: CreateDepartmentType = async (
    jwt,
    institutionId,
    name,
) => {
    const url = new InstitutionUrl().create_department;
    if (!jwt) {
        return { status: 401, error: "JWT has expired" };
    }

    let response: AxiosResponse<any>;
    try {
        response = await axios.post<CreateDepartmentApiResponse | string>(
            url,
            { institution_id: institutionId, name },
            {
                headers: {
                    "Content-Type": "application/json",
                    token: jwt,
                },
                validateStatus: () => true, // Resolve promise for all HTTP status codes
            },
        );
    } catch (error) {
        return {
            status: 500,
            error: "Network or other error occurred",
        };
    }

    return response.status === 201
        ? {
              status: response.status,
              department: response.data as CreateDepartmentResponse,
          }
        : { status: response.status, error: response.data as string };
};
